import { useCallback } from "react";
import { v4 as uuid } from "uuid";
import useTrackerContext from "./useTrackerContext.js";
import { InternalEventsUtils } from "@flights/web-api-utils-universal";
import { clientFetch } from "@flights/client-fetch";
import useGlobalContext from "./useGlobalContext.js";
import usePointOfSale from "./usePointOfSale.js";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import { GOOGLE_TAG_MANAGER_CONTAINER_ID, GOOGLE_TAG_MANAGER_CONTAINER_LABEL, SKYSCANNER_EXT_ORIGIN_PARAM_VALUES } from "@flights/constants";
const debug = require("debug")("useEventTracking");
let LAST_SEEN_CONTEXT_NAME;
function useContextEventTracking(contextName) {
    const track = (payload) => {
        const timestamp = Date.now();
        const eventId = uuid();
        const avroEvent = {
            generic: {
                epoch_ms: timestamp,
                event_id: eventId
            },
            context: Object.assign({}, payload, { context_id: eventId })
        };
        LAST_SEEN_CONTEXT_NAME = contextName;
        void sendAvroEvent(contextName, avroEvent);
    };
    return useCallback(track, [contextName]);
}
const C360_TRACKED_COMPONENTS = new Set(["live_chat"]);
function useEventTracking(componentName, requestId) {
    const pointOfSale = usePointOfSale();
    const adplat = getMarketingTrackingVariable("adplat");
    const { features } = useGlobalContext();
    const track = (event, payload) => {
        const context = InternalEventsUtils.getFlightEventsContext(LAST_SEEN_CONTEXT_NAME, requestId, "ACTION", event, payload, pointOfSale, adplat);
        const timestamp = Date.now();
        const avroEvent = {
            generic: {
                epoch_ms: timestamp,
                event_id: uuid()
            },
            context
        };
        const shouldTrackViaC360 = features.FLIGHTS_C360_FORWARD_ALL_EVENTS || C360_TRACKED_COMPONENTS.has(componentName);
        void sendAvroEvent(LAST_SEEN_CONTEXT_NAME, avroEvent, shouldTrackViaC360);
        return undefined;
    };
    return useCallback(track, [requestId, pointOfSale, adplat, componentName, features]);
}
const sendAvroEvent = async function (contextName, avroEvent, trackViaC360) {
    try {
        const url = "/track/internal-events";
        await clientFetch(url, {
            method: "POST",
            credentials: "include",
            referrerPolicy: "strict-origin-when-cross-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                context_name: contextName,
                data: avroEvent,
                ...(trackViaC360 && { trackViaC360: true })
            })
        });
    }
    catch (err) { }
};
function useTransactionTracking() {
    const { trackExternal } = useTrackerContext();
    const { features, lang = "", payerId = "", ipCountry = "", marketingTrackingVariables = {}, customerIp = "" } = useGlobalContext();
    return useCallback(async (transaction) => {
        const { id, revenue, currency, obfuscatedOtaOrderReference, partner = "", maskedPhoneNumber = "", maskedUserEmail = "" } = transaction;
        debug(`transaction: ${JSON.stringify(transaction)}`);
        trackExternal({
            type: "gtag",
            args: [
                "event",
                "conversion",
                {
                    send_to: `${GOOGLE_TAG_MANAGER_CONTAINER_ID}/${GOOGLE_TAG_MANAGER_CONTAINER_LABEL}`,
                    value: revenue,
                    currency: currency,
                    transaction_id: id
                }
            ]
        });
        if (features.FLIGHTS_META_SKYSCANNER_SEND_CONFIRMATION_EVENT &&
            SKYSCANNER_EXT_ORIGIN_PARAM_VALUES.includes(partner) &&
            obfuscatedOtaOrderReference) {
            trackExternal({
                type: "gtm-skyscanner-confirmation",
                args: [
                    {
                        event: "skyscanner-order-confirmation",
                        orderReference: obfuscatedOtaOrderReference
                    }
                ]
            });
        }
        const extraParams = { customerIp: "", customerEmail: "", customerPhoneNumber: "" };
        extraParams.customerIp = customerIp;
        extraParams.customerEmail = maskedUserEmail;
        extraParams.customerPhoneNumber = maskedPhoneNumber;
        if (features.FLIGHTS_MARKETING_BYEAH_SEND_CONFIRMATION_EVENT) {
            const bYeahTrackParams = {
                affiliateId: marketingTrackingVariables.aid || "",
                cookieId: payerId,
                label: marketingTrackingVariables.label || "",
                lang,
                userAgent: navigator?.userAgent,
                userLocation: ipCountry,
                offerCurrency: currency,
                offerPrice: revenue,
                orderReference: id,
                ...extraParams
            };
            trackExternal({
                type: "gtm-byeah-confirmation",
                args: [
                    {
                        event: "flights-byeah-order-confirmation",
                        ...bYeahTrackParams
                    }
                ]
            });
        }
    }, [trackExternal, features, lang, payerId, ipCountry, marketingTrackingVariables, customerIp]);
}
export default useEventTracking;
export { useContextEventTracking, useTransactionTracking };
